import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Alert, Divider, FormControl, InputLabel, Select, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Stepper, Step, StepLabel, Chip, Stack,
  Avatar, CardHeader, InputAdornment, LinearProgress
} from "@mui/material";
import { 
  ArrowBack, Add, Remove, PhotoCamera, CheckCircle, LocalShipping, Business,
  Inventory, CloudUpload, Warning, TrendingUp, Receipt, Description
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import {
  getLinenVendors,
  getDispatchesByVendor,
  getDispatchDetails,
  uploadLinenImages,
  receiveLinen
} from "../../services/api";

export const LinenReceive = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [loadingDispatches, setLoadingDispatches] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState("");
  const [dispatches, setDispatches] = useState([]);
  const [selectedDispatch, setSelectedDispatch] = useState("");
  const [dispatchDetails, setDispatchDetails] = useState(null);
  const [items, setItems] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const steps = ["Select Vendor", "Select Dispatch", "Record Receipt", "Review"];

  // Load vendors on component mount
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        const vRes = await getLinenVendors({ status: "active" });
        setVendors(vRes.data?.vendors || []);
      } catch (err) {
        console.error("Load vendors error:", err);
        enqueueSnackbar("❌ Unable to load vendors", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [enqueueSnackbar]);

  // Load dispatches when vendor is selected
  const loadDispatches = useCallback(async (vendorId) => {
    if (!vendorId) {
      setDispatches([]);
      setSelectedDispatch("");
      return;
    }

    try {
      setLoadingDispatches(true);
      const dRes = await getDispatchesByVendor(vendorId, {
        status: ['dispatched', 'partial_received']
      });
      const dispatchesData = dRes.data?.dispatches || [];

      // Sort by creation date, newest first
      const sortedDispatches = dispatchesData.sort((a, b) =>
        new Date(b.createdAt || b.pickup_time) - new Date(a.createdAt || a.pickup_time)
      );

      setDispatches(sortedDispatches);

      if (sortedDispatches.length === 0) {
        enqueueSnackbar("ℹ️ No receivable dispatches found for this vendor", { variant: "info" });
      }
    } catch (err) {
      console.error("Load dispatches error:", err);
      enqueueSnackbar("❌ Unable to load dispatches for this vendor", { variant: "error" });
      setDispatches([]);
    } finally {
      setLoadingDispatches(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadDispatches(selectedVendor);
  }, [selectedVendor, loadDispatches]);

  // Load dispatch details when dispatch is selected
  const loadDispatchDetails = useCallback(async (dispatchId) => {
    if (!dispatchId) {
      setDispatchDetails(null);
      setItems([]);
      return;
    }

    try {
      setLoading(true);
      const res = await getDispatchDetails(dispatchId);
      const dispatch = res.data?.dispatch;

      if (dispatch) {
        setDispatchDetails(dispatch);

        // Initialize items with proper structure from dispatch items
        const normalizedItems = (dispatch.items || []).map((item, index) => ({
          _id: item._id || item.linen?._id || `item-${index}-${Date.now()}`,
          linen: item.linen,
          sentQty: item.quantity || 0,
          receivedQty: 0,
          damagedQty: 0,
          missingQty: 0,
          weight: item.weight || 0,
          notes: "",
        }));

        setItems(normalizedItems);
      }
    } catch (err) {
      console.error("Load dispatch details error:", err);
      enqueueSnackbar("❌ Unable to load dispatch details", { variant: "error" });
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Handle dispatch selection
  const handleDispatchSelect = (dispatchId) => {
    setSelectedDispatch(dispatchId);
    if (dispatchId) {
      loadDispatchDetails(dispatchId);
    }
  };

  // Item quantity handlers
  const changeReceived = (index, value) => {
    const arr = [...items];
    arr[index].receivedQty = Math.max(0, Number(value));
    setItems(arr);
  };

  const changeDamaged = (index, value) => {
    const arr = [...items];
    arr[index].damagedQty = Math.max(0, Number(value));
    setItems(arr);
  };

  const changeMissing = (index, value) => {
    const arr = [...items];
    arr[index].missingQty = Math.max(0, Number(value));
    setItems(arr);
  };

  const changeWeight = (index, value) => {
    const arr = [...items];
    arr[index].weight = Math.max(0, Number(value));
    setItems(arr);
  };

  const changeNotes = (index, value) => {
    const arr = [...items];
    arr[index].notes = value;
    setItems(arr);
  };

  const handlePhotosChange = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos(files);
  };

  const handleConfirmReceive = async () => {
    setSaving(true);
    try {
      let uploadedPhotos = [];

      // Upload photos if any
      if (photos.length > 0) {
        const formData = new FormData();
        photos.forEach((file) => formData.append("images", file));

        const uploadRes = await uploadLinenImages(formData);

        const uploadedUrls =
          uploadRes.data?.files?.map(f => f.url) ||
          uploadRes.data?.urls ||
          [];

        uploadedPhotos = uploadedUrls;
      }

      const payload = {
        vendor: selectedVendor,
        dispatch: selectedDispatch,
        received_at: new Date().toISOString(),
        photos: uploadedPhotos,
        items: items.map((item) => ({
          linen: item.linen._id,
          sentQty: item.sentQty,
          receivedQty: Number(item.receivedQty) || 0,
          damagedQty: Number(item.damagedQty) || 0,
          missingQty: Number(item.missingQty) || 0,
          weight: Number(item.weight) || 0,
          notes: item.notes || "",
        })),
      };

      const result = await receiveLinen(payload);

      if (result.data.success) {
        enqueueSnackbar(result.data.message || "🎉 Linen received successfully", { variant: "success" });
        setOpenConfirm(false);
        resetForm();
      } else {
        throw new Error(result.data.message || "Receive failed");
      }
    } catch (err) {
      console.error("Receive error:", err);
      enqueueSnackbar(
        err.response?.data?.message || err.message || "❌ Receive failed",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setSelectedVendor("");
    setSelectedDispatch("");
    setDispatchDetails(null);
    setDispatches([]);
    setItems([]);
    setPhotos([]);
    setActiveStep(0);
  };

  const nextStep = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  // Validate before moving to review
  const validateReceipt = () => {
    if (items.length === 0) {
      enqueueSnackbar("⚠️ No items to receive", { variant: "warning" });
      return false;
    }

    const hasReceipt = items.some(item =>
      Number(item.receivedQty) > 0 ||
      Number(item.damagedQty) > 0 ||
      Number(item.missingQty) > 0
    );

    if (!hasReceipt) {
      enqueueSnackbar("⚠️ Please enter received quantities for at least one item", { variant: "warning" });
      return false;
    }

    // Validate that received + damaged + missing doesn't exceed sent
    const invalidItems = items.filter(item => {
      const totalReceived = Number(item.receivedQty) + Number(item.damagedQty) + Number(item.missingQty);
      return totalReceived > Number(item.sentQty);
    });

    if (invalidItems.length > 0) {
      enqueueSnackbar("❌ Received + Damaged + Missing cannot exceed Sent quantity", { variant: "error" });
      return false;
    }

    return true;
  };

  const handleReview = () => {
    if (validateReceipt()) {
      nextStep();
    }
  };

  // Calculate totals
  const totals = items.reduce(
    (acc, item) => {
      acc.sent += Number(item.sentQty || 0);
      acc.received += Number(item.receivedQty || 0);
      acc.damaged += Number(item.damagedQty || 0);
      acc.missing += Number(item.missingQty || 0);
      acc.weight += Number(item.weight || 0);
      return acc;
    },
    { sent: 0, received: 0, damaged: 0, missing: 0, weight: 0 }
  );

  const selectedVendorObj = vendors.find(v => v._id === selectedVendor);
  const selectedDispatchObj = dispatches.find(d => d._id === selectedDispatch);

  if (loading && activeStep === 0) {
    return (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        minHeight: 400,
        flexDirection: 'column',
        gap: 3
      }}>
        <CircularProgress size={60} thickness={4} />
        <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
          Loading Linen Receive...
        </Typography>
        <Typography variant="h6" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>
          Preparing vendors and dispatch data
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, backgroundColor: 'grey.50', minHeight: '100vh' }}>
      {/* Header */}
      <Card sx={{ 
        mb: 4, 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        borderRadius: 3
      }}>
        <CardContent sx={{ p: 4 }}>
          <Grid container alignItems="center" spacing={3}>
            <Grid item>
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Receipt sx={{ fontSize: 48, color: 'white' }} />
              </Avatar>
            </Grid>
            <Grid item xs>
              <Typography 
                variant="h2" 
                fontWeight={800}
                sx={{ 
                  color: 'white',
                  fontSize: { xs: '2.5rem', md: '3rem' },
                  mb: 1
                }}
              >
                Receive Linen
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'white', 
                  opacity: 0.9,
                  fontSize: '1.3rem'
                }}
              >
                Process linen returns from laundry vendors
              </Typography>
            </Grid>
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton 
                  onClick={prevStep} 
                  disabled={activeStep === 0}
                  sx={{ 
                    backgroundColor: 'white',
                    color: 'primary.main',
                    '&:hover': { backgroundColor: 'grey.100' }
                  }}
                >
                  <ArrowBack sx={{ fontSize: 28 }} />
                </IconButton>
                
                {/* Progress Info */}
                {activeStep > 0 && (
                  <Stack direction="row" spacing={1}>
                    {selectedVendorObj && (
                      <Chip
                        icon={<Business sx={{ fontSize: 20 }} />}
                        label={selectedVendorObj.name}
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                      />
                    )}
                    {selectedDispatchObj && (
                      <Chip
                        icon={<LocalShipping sx={{ fontSize: 20 }} />}
                        label={`Dispatch: ${selectedDispatchObj.dispatchNumber || selectedDispatchObj._id}`}
                        color="secondary"
                        variant="outlined"
                        sx={{ fontSize: '1.1rem', fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Stepper */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
        <CardContent sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel 
                  sx={{ 
                    '& .MuiStepLabel-label': { 
                      fontSize: '1.2rem', 
                      fontWeight: 600 
                    } 
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step 1: Select Vendor */}
      {activeStep === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Business sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                      Select Vendor
                    </Typography>
                  </Box>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50'
                }}
              />
              <CardContent>
                <FormControl fullWidth size="medium" sx={{ mt: 2 }}>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Vendor *</InputLabel>
                  <Select
                    value={selectedVendor}
                    label="Vendor *"
                    onChange={(e) => setSelectedVendor(e.target.value)}
                    sx={{ fontSize: '1.1rem' }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Business sx={{ color: 'primary.main', fontSize: 28 }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="" sx={{ fontSize: '1.1rem' }}>-- Select Vendor --</MenuItem>
                    {vendors.map(vendor => (
                      <MenuItem key={vendor._id} value={vendor._id} sx={{ fontSize: '1.1rem' }}>
                        {vendor.name}
                        {vendor.vendorCode && (
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontSize: '1rem' }}>
                            ({vendor.vendorCode})
                          </Typography>
                        )}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                  <Button 
                    variant="outlined" 
                    onClick={prevStep} 
                    disabled
                    sx={{ 
                      fontSize: '1.1rem',
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={nextStep}
                    disabled={!selectedVendor}
                    sx={{ 
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Next: Select Dispatch
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              position: 'sticky',
              top: 100
            }}>
              <CardHeader
                title={
                  <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                    ℹ️ Information
                  </Typography>
                }
              />
              <CardContent>
                <Alert severity="info" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
                  Select the vendor from whom you are receiving linen. Only active vendors are shown.
                </Alert>
                <Stack spacing={1}>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                    • Required fields are marked with *
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                    • Only vendors with active dispatches will be available
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                    • Dispatches must be in 'dispatched' or 'partial_received' status
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 2: Select Dispatch */}
      {activeStep === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LocalShipping sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                      Select Dispatch
                    </Typography>
                  </Box>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50'
                }}
              />
              <CardContent>
                <FormControl fullWidth size="medium" sx={{ mt: 2 }} disabled={!selectedVendor || loadingDispatches}>
                  <InputLabel sx={{ fontSize: '1.1rem' }}>Dispatch *</InputLabel>
                  <Select
                    value={selectedDispatch}
                    label="Dispatch *"
                    onChange={(e) => handleDispatchSelect(e.target.value)}
                    sx={{ fontSize: '1.1rem' }}
                    startAdornment={
                      <InputAdornment position="start">
                        <LocalShipping sx={{ color: 'primary.main', fontSize: 28 }} />
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="" sx={{ fontSize: '1.1rem' }}>-- Select Dispatch --</MenuItem>
                    {dispatches.map(dispatch => (
                      <MenuItem key={dispatch._id} value={dispatch._id} sx={{ fontSize: '1.1rem' }}>
                        {dispatch.dispatchNumber || `Dispatch-${dispatch._id.slice(-6)}`}
                        <Typography variant="body2" color="text.secondary" sx={{ ml: 1, fontSize: '1rem' }}>
                          ({new Date(dispatch.pickup_time || dispatch.createdAt).toLocaleDateString()}) - {dispatch.status}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  {loadingDispatches && (
                    <CircularProgress size={24} sx={{ position: 'absolute', right: 40, top: '50%', transform: 'translateY(-50%)' }} />
                  )}
                </FormControl>

                {dispatches.length === 0 && selectedVendor && !loadingDispatches && (
                  <Alert severity="warning" sx={{ mt: 3, fontSize: '1.1rem', borderRadius: 2 }}>
                    ⚠️ No receivable dispatches found for this vendor. Dispatches must be in 'dispatched' or 'partial_received' status.
                  </Alert>
                )}

                <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                  <Button 
                    variant="outlined" 
                    onClick={prevStep}
                    sx={{ 
                      fontSize: '1.1rem',
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={nextStep}
                    disabled={!selectedDispatch || items.length === 0}
                    sx={{ 
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Next: Record Receipt
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              position: 'sticky',
              top: 100
            }}>
              <CardHeader
                title={
                  <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                    📋 Dispatch Information
                  </Typography>
                }
              />
              <CardContent>
                {selectedDispatchObj ? (
                  <Box sx={{ p: 2, backgroundColor: 'success.light', borderRadius: 2 }}>
                    <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 700, mb: 1 }}>
                      🚚 {selectedDispatchObj.dispatchNumber || selectedDispatchObj._id}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>📅 Pickup Time:</strong> {new Date(selectedDispatchObj.pickup_time || selectedDispatchObj.createdAt).toLocaleString()}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>📊 Status:</strong> {selectedDispatchObj.status}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      <strong>📦 Items:</strong> {selectedDispatchObj.items?.length || 0}
                    </Typography>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ fontSize: '1.1rem' }}>
                    Select a dispatch to view details
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Step 3: Record Receipt */}
      {activeStep === 2 && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          <CardHeader
            title={
              <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                📝 Record Received Quantities
              </Typography>
            }
            sx={{
              borderBottom: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.50'
            }}
          />
          <CardContent>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6, flexDirection: 'column', gap: 2 }}>
                <CircularProgress size={60} thickness={4} />
                <Typography variant="h4" sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
                  Loading Dispatch Details...
                </Typography>
              </Box>
            ) : items.length === 0 ? (
              <Alert severity="info" sx={{ fontSize: '1.1rem', borderRadius: 3 }}>
                📦 No items available in this dispatch
              </Alert>
            ) : (
              <>
                <Alert severity="info" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
                  📊 Enter the quantities received for each item. Received + Damaged + Missing should not exceed Sent quantity.
                </Alert>

                <TableContainer component={Paper} variant="outlined" sx={{ mt: 2, borderRadius: 2 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: 'primary.main' }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📦 Item
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📤 Sent Qty
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          ✅ Received
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🟡 Damaged
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🔴 Missing
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          ⚖️ Weight (kg)
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📝 Notes
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((item, index) => {
                        const totalReceived = Number(item.receivedQty) + Number(item.damagedQty) + Number(item.missingQty);
                        const exceedsSent = totalReceived > Number(item.sentQty);

                        return (
                          <TableRow 
                            key={item._id} 
                            hover 
                            sx={{ 
                              bgcolor: exceedsSent ? 'error.light' : 'transparent',
                              '&:hover': {
                                backgroundColor: exceedsSent ? 'error.light' : 'primary.light',
                                transform: 'translateY(-1px)',
                                transition: 'all 0.3s ease'
                              },
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <TableCell>
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                                {item.linen?.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                {item.linen?.code || 'No Code'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                                {item.sentQty}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                                <IconButton
                                  size="large"
                                  onClick={() => changeReceived(index, Math.max(0, (Number(item.receivedQty) || 0) - 1))}
                                  sx={{ 
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'primary.dark' }
                                  }}
                                >
                                  <Remove sx={{ fontSize: 20 }} />
                                </IconButton>
                                <TextField
                                  value={item.receivedQty}
                                  onChange={(e) => changeReceived(index, e.target.value)}
                                  type="number"
                                  sx={{ width: 100 }}
                                  inputProps={{ 
                                    min: 0, 
                                    max: item.sentQty,
                                    style: { fontSize: '1.1rem', textAlign: 'center' }
                                  }}
                                  error={exceedsSent}
                                />
                                <IconButton
                                  size="large"
                                  onClick={() => changeReceived(index, (Number(item.receivedQty) || 0) + 1)}
                                  sx={{ 
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { backgroundColor: 'primary.dark' }
                                  }}
                                >
                                  <Add sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                value={item.damagedQty}
                                onChange={(e) => changeDamaged(index, e.target.value)}
                                type="number"
                                sx={{ width: 100 }}
                                inputProps={{ 
                                  min: 0,
                                  style: { fontSize: '1.1rem', textAlign: 'center' }
                                }}
                                error={exceedsSent}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                value={item.missingQty}
                                onChange={(e) => changeMissing(index, e.target.value)}
                                type="number"
                                sx={{ width: 100 }}
                                inputProps={{ 
                                  min: 0,
                                  style: { fontSize: '1.1rem', textAlign: 'center' }
                                }}
                                error={exceedsSent}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                value={item.weight}
                                onChange={(e) => changeWeight(index, e.target.value)}
                                type="number"
                                sx={{ width: 120 }}
                                inputProps={{ 
                                  min: 0, 
                                  step: 0.1,
                                  style: { fontSize: '1.1rem', textAlign: 'center' }
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                value={item.notes}
                                onChange={(e) => changeNotes(index, e.target.value)}
                                fullWidth
                                size="medium"
                                placeholder="Notes..."
                                InputProps={{
                                  sx: { fontSize: '1.1rem' }
                                }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Photo Upload */}
                <Box sx={{ 
                  mt: 4, 
                  p: 3, 
                  border: '2px dashed', 
                  borderColor: 'primary.main', 
                  borderRadius: 3,
                  backgroundColor: 'primary.light'
                }}>
                  <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                    📸 Upload Photos (Optional)
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUpload sx={{ fontSize: 28 }} />}
                    sx={{ 
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      px: 3,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Upload Photos
                    <input
                      hidden
                      accept="image/*"
                      multiple
                      type="file"
                      onChange={handlePhotosChange}
                    />
                  </Button>
                  {photos.length > 0 && (
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'success.main', mt: 2 }}>
                      ✅ {photos.length} file(s) selected
                    </Typography>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                  <Button 
                    variant="outlined" 
                    onClick={prevStep}
                    sx={{ 
                      fontSize: '1.1rem',
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Back
                  </Button>
                  <Button 
                    variant="contained" 
                    onClick={handleReview}
                    sx={{ 
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Next: Review
                  </Button>
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review */}
      {activeStep === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
              <CardHeader
                title={
                  <Typography variant="h4" sx={{ fontSize: '1.6rem', fontWeight: 700 }}>
                    👁️ Review Receive
                  </Typography>
                }
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'grey.50'
                }}
              />
              <CardContent>
                <Alert severity="warning" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
                  ⚠️ Please review all information before confirming. This action will update inventory and cannot be undone.
                </Alert>

                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                          🏢 Vendor & Dispatch
                        </Typography>
                        <Stack spacing={1}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            <strong>🏭 Vendor:</strong> {selectedVendorObj?.name}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            <strong>🚚 Dispatch:</strong> {selectedDispatchObj?.dispatchNumber || selectedDispatchObj?._id}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            <strong>📅 Received Date:</strong> {new Date().toLocaleString()}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 2 }}>
                          📊 Summary
                        </Typography>
                        <Stack spacing={1}>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            <strong>📦 Total Items:</strong> {items.length}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            <strong>✅ Total Received:</strong> {totals.received}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            <strong>🟡 Total Damaged:</strong> {totals.damaged}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            <strong>🔴 Total Missing:</strong> {totals.missing}
                          </Typography>
                          <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                            <strong>⚖️ Total Weight:</strong> {totals.weight.toFixed(2)} kg
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead sx={{ backgroundColor: 'primary.main' }}>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📦 Item
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📤 Sent
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          ✅ Received
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🟡 Damaged
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          🔴 Missing
                        </TableCell>
                        <TableCell align="center" sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          ⚖️ Weight
                        </TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 800, fontSize: '1.2rem', py: 3 }}>
                          📝 Notes
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map(item => (
                        <TableRow 
                          key={item._id} 
                          hover
                          sx={{ 
                            '&:hover': {
                              backgroundColor: 'primary.light',
                              transform: 'translateY(-1px)',
                              transition: 'all 0.3s ease'
                            },
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <TableCell>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {item.linen?.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '1rem' }}>
                              {item.linen?.code || 'No Code'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {item.sentQty}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'success.main' }}>
                              {item.receivedQty}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'warning.main' }}>
                              {item.damagedQty}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'error.main' }}>
                              {item.missingQty}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {item.weight || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                              {item.notes || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ backgroundColor: 'warning.light' }}>
                        <TableCell>
                          <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                            📊 Totals
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                            {totals.sent}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 800, color: 'success.main' }}>
                            {totals.received}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 800, color: 'warning.main' }}>
                            {totals.damaged}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 800, color: 'error.main' }}>
                            {totals.missing}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 800 }}>
                            {totals.weight.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {photos.length > 0 && (
                  <Box sx={{ mt: 3, p: 2, backgroundColor: 'info.light', borderRadius: 2 }}>
                    <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700, mb: 1 }}>
                      📸 Photos ({photos.length})
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                      {photos.length} photo(s) will be uploaded with this receipt.
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", gap: 2, mt: 4 }}>
                  <Button 
                    variant="outlined" 
                    onClick={prevStep}
                    sx={{ 
                      fontSize: '1.1rem',
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Back to Editing
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle sx={{ fontSize: 28 }} />}
                    onClick={() => setOpenConfirm(true)}
                    sx={{ 
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      px: 4,
                      py: 1.5,
                      borderRadius: 3
                    }}
                  >
                    Confirm Receive
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
              position: 'sticky',
              top: 100
            }}>
              <CardHeader
                title={
                  <Typography variant="h5" sx={{ fontSize: '1.4rem', fontWeight: 700 }}>
                    ✅ Ready to Submit
                  </Typography>
                }
              />
              <CardContent>
                <Alert severity="success" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
                  🎉 All information has been validated and is ready for submission.
                </Alert>
                <Stack spacing={1}>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                    • 📦 Inventory will be updated automatically
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                    • ✅ Dispatch status will be marked as received
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                    • 📝 Transaction history will be recorded
                  </Typography>
                  <Typography variant="body1" sx={{ fontSize: '1.1rem', fontWeight: 600, color: 'text.secondary' }}>
                    • 📧 Managers will be notified via email
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Confirmation Dialog */}
      <Dialog 
        open={openConfirm} 
        onClose={() => !saving && setOpenConfirm(false)} 
        maxWidth="sm" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'warning.main', 
          color: 'white',
          py: 3
        }}>
          <Typography variant="h3" sx={{ fontSize: '1.8rem', fontWeight: 700 }}>
            ⚠️ Confirm Linen Receive
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Alert severity="warning" sx={{ mb: 3, fontSize: '1.1rem', borderRadius: 2 }}>
            This action will update inventory and cannot be undone. Please confirm all details are correct.
          </Alert>
          <Stack spacing={1}>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              <strong>🏭 Vendor:</strong> {selectedVendorObj?.name}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              <strong>🚚 Dispatch:</strong> {selectedDispatchObj?.dispatchNumber || selectedDispatchObj?._id}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              <strong>📦 Items:</strong> {items.length}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              <strong>✅ Total Received:</strong> {totals.received}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              <strong>🟡 Total Damaged:</strong> {totals.damaged}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              <strong>🔴 Total Missing:</strong> {totals.missing}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem', fontWeight: 600 }}>
              <strong>📸 Photos:</strong> {photos.length}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setOpenConfirm(false)} 
            disabled={saving}
            sx={{ 
              fontSize: '1.1rem',
              px: 4,
              py: 1
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <CheckCircle sx={{ fontSize: 24 }} />}
            onClick={handleConfirmReceive}
            disabled={saving}
            color="primary"
            sx={{ 
              fontSize: '1.2rem',
              fontWeight: 600,
              px: 4,
              py: 1,
              minWidth: 200
            }}
          >
            {saving ? "Processing..." : "Confirm Receive"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};